// Comprehensive CMS Store.
//
// Reads are backed by localStorage (sync) so all existing callers keep
// working unchanged. Writes are mirrored to the backend via /api/*
// (fire-and-forget) and emit a cmsEvents change signal so any frontend
// section subscribed via useCollection / useContentSection refreshes.
//
// The backend is the source of truth on page load — see lib/hydrate.ts,
// which runs at app boot and replaces localStorage with live server data.
//
// This file is the public barrel: every existing consumer (`@/lib/cmsStore`)
// keeps working unchanged. Larger domains live in sibling files:
//   - cmsStore.shared.ts        — internal: syncToServer, STORAGE_KEYS
//   - cmsStore.conversations.ts — conversations CRUD + types
//   - cmsStore.media.ts         — media library CRUD + types
//   - cmsStore.sections.ts      — content sections CRUD + types

import type { Article } from '@/types/article';
import type { Service } from '@/types/service';
import { apiPut } from './api';
import { syncToServer, STORAGE_KEYS } from './cmsStore.shared';
import { getConversations } from './cmsStore.conversations';
import { getMediaLibrary } from './cmsStore.media';

// Modern reactive cache shared with useCollection. Writes go through the
// cmsCollection* helpers in cmsStore.collections; the sync read helpers below
// (getCmsArticles, getCmsServices, getCmsSocialLinks) point at this so any
// non-React caller — lib/articles.ts, lib/services.ts — sees the same state
// the React tree sees, including optimistic writes.
const COLL_CACHE_PREFIX = 'ck_cache_coll_';

function readCollectionCache<T>(scope: string): T[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(`${COLL_CACHE_PREFIX}${scope}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

// Re-export the public surface of each sub-module so consumers keep
// importing everything from '@/lib/cmsStore'. The sections sub-module was
// retired with the admin-consolidation v1 pass — content_sections is no
// longer canonical for any public surface.
export * from './cmsStore.conversations';
export * from './cmsStore.media';
export * from './cmsStore.collections';

// ─── Types ───────────────────────────────────────────────

export interface NotificationChannel {
  type: 'email' | 'telegram' | 'whatsapp';
  enabled: boolean;
  config: Record<string, string>;
}

export interface CMSSettings {
  adminPassword: string;
  siteTitle: string;
  siteDescription: string;
  deepseekApiKey: string;
  aiEnabled: boolean;
  aiSystemPrompt: string;
  aiModel: string;
  notifications: NotificationChannel[];
  notificationTriggers: {
    onFlaggedConversation: boolean;
    onUrgentRequest: boolean;
    onContactRequest: boolean;
    onFirstMessage: boolean;
  };
  contactEmail: string;
  contactTelegram: string;
  contactWhatsApp: string;
}

// ─── Default Settings ──────────────────────────────────────

const DEFAULT_SETTINGS: CMSSettings = {
  adminPassword: 'admin123', // In production, hash this
  siteTitle: 'Chinedu Koggu — FinTech Techpreneur',
  siteDescription: 'Chinedu Koggu — FinTech & Platform Techpreneur | CTO at TechMasters Zambia | Alibaba eFounder Fellow',
  deepseekApiKey: '',
  aiEnabled: true,
  aiModel: 'deepseek-chat',
  aiSystemPrompt: `You are Chinedu Koggu's AI assistant on his personal brand website. You represent Chinedu Koggu professionally and helpfully.

About Chinedu Koggu:
- FinTech & Platform Techpreneur based in Lusaka, Zambia
- CTO at TechMasters Zambia Limited (May 2023 - Present)
- Co-Founder & CTO at ProBASE Limited (2010-2023), 14 years architecting payment platforms
- Alibaba eFounder Fellow (prestigious program by Alibaba Business School and UNCTAD)
- Partner at C2M Networks Ltd (Business Solutions)
- B.Eng. Electrical Electronics Engineering from FUTO (1997-2001)
- Over 20 years of experience in fintech and platform architecture
- Led VISA/MasterCard implementations reaching 5M+ cards across Africa at Unified Payments
- Active mentor at BongoHive specializing in Business Model Optimization and Product Development
- Co-host of Three & Company Podcast with George Mwale
- Frequent speaker at Zambia Bankers Association, DataHack4FI, Flutterwave Fintech Mixers, TechPay launches

Core Philosophy: "I don't just build products — I design systems of freedom."

Areas of Expertise:
- Payment system architecture (multi-rail platforms, card issuing, collections)
- Mobile payments and digital wallets
- Process automation and digital transformation
- Business model optimization
- Mentorship and coaching for tech entrepreneurs
- Fintech strategy and implementation

Thought Leadership Themes:
1. The Artisan Economy - integrating informal sector artisans into the digital economy
2. FinTech Realities - startups fail from cash flow friction, not product competition
3. Anti-Conformity - "Do not announce — just do it and let the doing announce itself"
4. Epistemology & Value Creation - questioning foundational nature of value, truth, and bias

When users ask to engage directly with Chinedu or have an important conversation, offer to route their message via email, Telegram, or WhatsApp. Collect their name and email if they want to be contacted back.

Be professional, warm, and knowledgeable. Use African tech context naturally. Keep responses concise but informative.`,
  notifications: [
    { type: 'email', enabled: true, config: { recipient: 'hello@chinedukoggu.com' } },
    { type: 'telegram', enabled: false, config: { botToken: '', chatId: '' } },
    { type: 'whatsapp', enabled: false, config: { phoneNumber: '' } },
  ],
  notificationTriggers: {
    onFlaggedConversation: true,
    onUrgentRequest: true,
    onContactRequest: true,
    onFirstMessage: false,
  },
  contactEmail: 'hello@chinedukoggu.com',
  contactTelegram: '',
  contactWhatsApp: '',
};

const LEGACY_PURGE_SENTINEL = 'ck_cms_legacy_purged';

/**
 * One-shot wipe of stale client-side caches that the new server-backed
 * Dashboard would otherwise show as phantom counts. Runs idempotently —
 * a sentinel key in localStorage guards against repeat invocations.
 * Settings, theme, and any genuinely client-only keys stay intact.
 */
export function purgeLegacyClientCache(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(LEGACY_PURGE_SENTINEL)) return;
    localStorage.removeItem(STORAGE_KEYS.conversations);
    localStorage.removeItem(STORAGE_KEYS.media);
    localStorage.setItem(LEGACY_PURGE_SENTINEL, new Date().toISOString().slice(0, 10));
  } catch {
    // best-effort — localStorage may be unavailable in private mode
  }
}

// ─── Social Links ──────────────────────────────────────────

export interface CmsSocialLink {
  id: string;
  label: string;
  href: string;
  iconName: string; // Lucide icon PascalCase name (e.g. "Linkedin", "Twitter")
  order: number;
  visible: boolean;
}

// Read-only accessor for non-React contexts. Reads from the same cache
// useCollection<SocialLinkRow>('social-links') feeds, so callers observe
// optimistic state during the mutation window. The row shape on the wire
// uses `sortOrder`; map to the legacy `order` field for back-compat.
export function getCmsSocialLinks(): CmsSocialLink[] {
  const rows = readCollectionCache<{
    id: string; label: string; href: string; iconName: string;
    sortOrder?: number; visible?: boolean;
  }>('social-links');
  return rows.map((r) => ({
    id: r.id,
    label: r.label,
    href: r.href,
    iconName: r.iconName,
    order: r.sortOrder ?? 0,
    visible: r.visible !== false,
  }));
}

// ─── Settings ──────────────────────────────────────────────

export function getSettings(): CMSSettings {
  const stored = localStorage.getItem(STORAGE_KEYS.settings);
  if (stored) {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  }
  localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(DEFAULT_SETTINGS));
  return DEFAULT_SETTINGS;
}

export function saveSettings(settings: CMSSettings): void {
  localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(settings));
  syncToServer(() => apiPut('/api/config/cms_settings', settings), 'config');
}

// ─── Auth ──────────────────────────────────────────────────

export function isAuthenticated(): boolean {
  return localStorage.getItem(STORAGE_KEYS.auth) === 'true';
}

export function logout(): void {
  localStorage.removeItem(STORAGE_KEYS.auth);
  // Tell Better-Auth to invalidate the session cookie. Fire-and-forget — if
  // it fails (e.g., offline) the localStorage flag is already cleared and the
  // server gate will redirect on next admin load anyway.
  void fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' }).catch(() => null);
}

// ─── Statistics ────────────────────────────────────────────

export function getDashboardStats() {
  const conversations = getConversations();
  const media = getMediaLibrary();
  const totalMessages = conversations.reduce((sum, c) => sum + c.messages.length, 0);
  const flaggedCount = conversations.filter((c) => c.flagged).length;
  const urgentCount = conversations.filter((c) => c.status === 'urgent').length;

  // Get conversations from last 7 days
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const recentConversations = conversations.filter((c) => c.lastMessageAt > weekAgo);

  return {
    totalConversations: conversations.length,
    totalMessages,
    flaggedCount,
    urgentCount,
    recentConversations: recentConversations.length,
    mediaCount: media.length,
    imageCount: media.filter((m) => m.type === 'image').length,
    videoCount: media.filter((m) => m.type === 'video').length,
    audioCount: media.filter((m) => m.type === 'audio').length,
  };
}

// ─── Articles ──────────────────────────────────────────────

// Read-only accessor for non-React contexts (lib/articles.ts). Reads from the
// useCollection-shared cache so callers observe optimistic state.
export function getCmsArticles(): Article[] {
  return readCollectionCache<Article>('articles');
}

export function getCmsArticleBySlug(slug: string): Article | undefined {
  return getCmsArticles().find((a) => a.slug === slug);
}

// ─── Services ──────────────────────────────────────────────

// Read-only accessor for non-React contexts (lib/services.ts). Reads from the
// useCollection-shared cache so callers observe optimistic state.
export function getCmsServices(): Service[] {
  return readCollectionCache<Service>('services');
}

export function getCmsServiceBySlug(slug: string): Service | undefined {
  return getCmsServices().find((s) => s.slug === slug);
}

// ─── Reset ─────────────────────────────────────────────────

export function resetAllData(): void {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem('ck_chat_session');
}
